import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import PageLayout from "@/components_v3/layout/PageLayout.vue";
import { getAffiliateLoginURL, getEmployeesAffiliate, patchUserByTypeV3 } from "@/api/user";
import { Affiliate, AFFILIATE_TABS } from "@/services/affiliates/Affiliate";
import store from "@/store";
import { removeAndInsetHtmlEl, showServerError } from "@/utils";
import { USER_ROLES } from "@/mappings/user-roles";
import BasePageTabs from "@/components-UI/BasePageTabs.vue";
import { updateQueryString } from "@/utils/updateQueryString";
import AffiliateSingle from "@/components/affiliate/AffiliateSingle.vue";
import Payments from "@/views/affiliates/payments/Payments.vue";
import Approvals from "@/views/offers/approval/Approvals.vue";
import PersonalRates from "@/views/offers/personalRate/PersonalRates.vue";
import PrivateRateList from "@/views/offers/private-rates/PrivateRateList.vue";
import AffiliatesList from "@/views/affiliates/AffiliatesList.vue";
import AffiliateHoldList from "@/views/affiliates/AffiliateHoldList.vue";
import AffiliateAdministration from "@/components/affiliate/AffiliateAdministration.vue";
import AffiliateChangeHistoryTable from "@/components/affiliate/AffiliateChangeHistoryTable.vue";
import BaseStatus from "@/components-UI/BaseStatus.vue";
import { translatePartnerStatus } from "@/utils/translate";
import BaseSwitch from "@/components-UI/BaseSwitch.vue";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";
import SvgUser from "@/assets/icons/payout/user.svg";
import AffiliateInvoices from "@/views/affiliates/invoices/AffiliateInvoices.vue";
import { AffiliateContestTab, ContestMonthPicker } from "@/components_v3/affiliate/contest";
import { getDefaultContestMonth } from "@/components_v3/affiliate/contest/shared/config/contest-months";
import { useColumnsStore } from "@/store_v3/ColumnsStore";
import { storeToRefs } from "pinia";
import { useWindowResize } from "@/utils/useWindowSize";


export default /*#__PURE__*/_defineComponent({
  __name: 'AffiliatePage',
  setup(__props) {

const { selected } = storeToRefs(useColumnsStore());
const { updateAllSelectedColumns } = useColumnsStore();
const { width } = useWindowResize();
const isMobile = computed(() => width.value < 768);

const route = useRoute();
const router = useRouter();
const user = store.getters['authModule/GET_USER'];
const affiliate = ref<Affiliate | null>(null);
const currentTab = ref(AFFILIATE_TABS.MAIN);
const contestMonth = ref(getDefaultContestMonth());
const isContestTabLoading = ref(false);

const hasContestTabRole = computed(() =>
    [USER_ROLES.ACCOUNT_MANAGER, USER_ROLES.ADMIN, USER_ROLES.AFFILIATE_MANAGER].some((role) => user.roles.includes(role)),
);

const isContestTabVisible = computed(() =>
    hasContestTabRole.value && !!affiliate.value?.is_master,
);

function onContestTabLoading(value: boolean): void {
    isContestTabLoading.value = value;
}

const tabs = computed(() => {
    const list = [
        { text: 'Партнёр', value: AFFILIATE_TABS.MAIN },
    ];

    if (user.roles.includes(USER_ROLES.ADMIN)) {
        list.push({ text: 'Холд', value: AFFILIATE_TABS.HOLD });
    }

    list.push({ text: 'Счета', value: AFFILIATE_TABS.INVOICES });

    if (isContestTabVisible.value) {
        list.push({ text: 'Конкурс', value: AFFILIATE_TABS.CONTEST });
    }

    list.push(
        { text: 'Досрочки', value: AFFILIATE_TABS.PAYMENTS },
        { text: 'Доступы к офферам', value: AFFILIATE_TABS.APPROVALS },
        { text: 'Индивидуальные цены', value: AFFILIATE_TABS.RATES },
        { text: 'Реестр цен', value: AFFILIATE_TABS.PRIVATE_RATES },
        { text: 'Мастер-аккаунт', value: AFFILIATE_TABS.MASTER },
    );

    if (user.roles.includes(USER_ROLES.ADMIN)) {
        list.push({ text: 'Администрирование', value: AFFILIATE_TABS.ADMIN });
    }

    list.push({ text: 'История изменений', value: AFFILIATE_TABS.HISTORY });

    return list;
});

const tabComponent = {
    [AFFILIATE_TABS.MAIN]: AffiliateSingle,
    [AFFILIATE_TABS.INVOICES]: AffiliateInvoices,
    [AFFILIATE_TABS.CONTEST]: AffiliateContestTab,
    [AFFILIATE_TABS.PAYMENTS]: Payments,
    [AFFILIATE_TABS.APPROVALS]: Approvals,
    [AFFILIATE_TABS.RATES]: PersonalRates,
    [AFFILIATE_TABS.PRIVATE_RATES]: PrivateRateList,
    [AFFILIATE_TABS.HOLD]: AffiliateHoldList,
    [AFFILIATE_TABS.MASTER]: AffiliatesList,
    [AFFILIATE_TABS.ADMIN]: AffiliateAdministration,
    [AFFILIATE_TABS.HISTORY]: AffiliateChangeHistoryTable,
};

const getClasses = computed(() => {
    return [
        currentTab.value === AFFILIATE_TABS.MAIN && '--main',
        currentTab.value === AFFILIATE_TABS.INVOICES && '--invoices',
        currentTab.value === AFFILIATE_TABS.CONTEST && '--contest',
        currentTab.value === AFFILIATE_TABS.PAYMENTS && '--payments',
        currentTab.value === AFFILIATE_TABS.APPROVALS && '--approvals',
        currentTab.value === AFFILIATE_TABS.RATES && '--rates',
        currentTab.value === AFFILIATE_TABS.PRIVATE_RATES && '--private-rates',
        currentTab.value === AFFILIATE_TABS.HOLD && '--hold',
        currentTab.value === AFFILIATE_TABS.MASTER && '--master',
        currentTab.value === AFFILIATE_TABS.ADMIN && '--admin',
        currentTab.value === AFFILIATE_TABS.HISTORY && '--history',
        currentTab.value === AFFILIATE_TABS.ADMIN && '--administration',
    ];
});

const pagesWithShiftedHeader = [
    AFFILIATE_TABS.INVOICES,
    AFFILIATE_TABS.PAYMENTS,
    AFFILIATE_TABS.APPROVALS,
    AFFILIATE_TABS.RATES,
    AFFILIATE_TABS.PRIVATE_RATES,
    AFFILIATE_TABS.HOLD,
    AFFILIATE_TABS.MASTER,
];

const affiliateId = computed(() => route.params.id);
const title = computed(() => currentTab.value === AFFILIATE_TABS.MAIN ? affiliate.value?.full_name : `${affiliate.value?.full_name} (${affiliateId.value})`);
const status = computed( () => translatePartnerStatus(affiliate.value?.status));

function backToList() {
    router.push({ name: 'affiliates-list' });
}

onBeforeMount( async () => {
    store.commit('SET_DEFAULT_FILTERS_PARAMS_OBJECT', {});
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    await getAffiliate();
    const requestedTab = route.query.tab as string || AFFILIATE_TABS.MAIN;
    const isTabAvailable = tabs.value.some((tab) => tab.value === requestedTab);
    currentTab.value = isTabAvailable ? requestedTab : AFFILIATE_TABS.MAIN;
    changeTab(currentTab.value);
});

onBeforeUnmount(() => {
    removeHeader();
});

watch(() => route.query, () => {
    setTabToUrl();
});

watch(isContestTabVisible, (visible) => {
    if (affiliate.value && !visible && currentTab.value === AFFILIATE_TABS.CONTEST) {
        changeTab(AFFILIATE_TABS.MAIN);
    }
});

const changeTab = (value: string) => {
    const isSameTab = currentTab.value === value;
    const isShiftedHeaderPage = pagesWithShiftedHeader.includes(value);
    currentTab.value = value;
    setTabToUrl();
    !isSameTab && removeHeader();
    isShiftedHeaderPage && insertHeader();
};

const insertHeader = () => {
    nextTick(() => {
        removeAndInsetHtmlEl({
            type: 'move',
            moveEl: '.base-list-layout__title-wrap',
            containerEl: '.page-layout__right',
        });
    });
};

const removeHeader = () => {
    removeAndInsetHtmlEl({
        type: 'remove',
        moveEl: '.base-list-layout__title-wrap',
        containerEl: '.page-layout__right',
    });
};

const setTabToUrl = () => {
    if (route.query.tab! === currentTab.value) return;
    nextTick(() => document.title = title.value);
    const params = { ...{ tab: currentTab.value } };
    // columns хранит выбранные колонки реестра цен, для остальных вкладок они чужие
    if (currentTab.value === AFFILIATE_TABS.PRIVATE_RATES) {
        const data = { ...store.getters['GET_FILTERS_PARAMS_OBJECT'] };
        const columns = selected.value;
        Object.assign(params, { ...data, columns });
    } else {
        updateAllSelectedColumns([]);
    }
    updateQueryString(params);
};

const getAffiliate = async () => {
    try {
        const data = await getEmployeesAffiliate(affiliateId.value);
        affiliate.value = new Affiliate(data);
        store.commit('SET_CURRENT_AFFILIATE', { id: affiliate.value.id, name: affiliate.value.full_name });
        store.commit('SET_IS_MASTER_AFFILIATE', !!affiliate.value.is_master);
        await affiliate.value.prepareAffiliateData();
    } catch (err) {
        showServerError(err, 'Партнер не найден');
    }
};

async function updateUser(): Promise<void> {
    try {
        await patchUserByTypeV3(affiliateId.value, 'affiliate', {
            disable_2fa: !affiliate.value.two_factor_enabled,
        });
    } catch (e) {
        showServerError(e, 'Не удалось включить');
    }
}

async function loginAsAffiliate(): Promise<void> {
    try {
        const { url } = await getAffiliateLoginURL(affiliate.value.id!);
        window.open(url, '_blank');
    } catch (err) {
        showServerError(err, 'Не удалось авторизоваться как партнёр');
    }
}


return { __sfc: true,selected, updateAllSelectedColumns, width, isMobile, route, router, user, affiliate, currentTab, contestMonth, isContestTabLoading, hasContestTabRole, isContestTabVisible, onContestTabLoading, tabs, tabComponent, getClasses, pagesWithShiftedHeader, affiliateId, title, status, backToList, changeTab, insertHeader, removeHeader, setTabToUrl, getAffiliate, updateUser, loginAsAffiliate, PageLayout, AFFILIATE_TABS, BasePageTabs, BaseStatus, BaseSwitch, BaseButtonV2, SvgUser, ContestMonthPicker }
}

})